import { useEffect, useState } from 'react'
import { api } from './api-generated.ts'
import settings from '../conf'

export const addDefaultArgs = (query, defaultArg) => {
  return (arg, options={}) => {
    const queryArgs = Object.assign({}, defaultArg)
      for (const [key, value] of Object.entries(arg)) {
        if (value) {
            queryArgs[key] = value
        }
      }
    options.refetchOnReconnect = navigator.onLine === false
    return query(queryArgs, options)
  }
}


const getData = (query, defaultArg={}) =>{
  return (arg, options={}) => {
    const queryArgs = Object.assign({}, defaultArg)
    if (arg){
      for (const [key, value] of Object.entries(arg)) {
        if (value === null || value === undefined) {
            continue
        } 
        queryArgs[key] = value
      }
    }
    options.refetchOnReconnect = navigator.onLine === false
    const {data, isLoading, isFetching, refetch, error} = query(queryArgs, options)

    if (navigator.onLine === false && error && !options.skip){
        window.addEventListener("online", (e) => {
          refetch()
        }, { once: true });
    }
    
    if (!options.keepOld && (isLoading || isFetching || options.skip)) return undefined
    return data
  }
}

const getActionData = (query, defaultArg={}) =>{
  return (options={}) => {
    options.refetchOnReconnect = navigator.onLine === false
    const [trigger, result, lastPromiseInfo] = query(options)
    const action = (arg) => {
      const queryArgs = Object.assign({}, defaultArg, arg)
      return trigger(queryArgs)
    }
    if (result.isLoading || result.isFetching ) return [action, undefined, true]
    return [action, result.data, false]
  }
}

const addTrailUUID = (query) => {
  return (args, options={}) => {
    options.refetchOnReconnect = navigator.onLine === false
    const {routeId, trailId} = args
    const route = useRoute({routeId: routeId}, {skip: options.skip || (!routeId || !trailId)})
    const trailUuid = args.trailUuid || trailId && route && route.trails[trailId - 1].uuid
    const queryArgs = {
      trailUuid: trailUuid
    }
    for (const [key, value] of Object.entries(args)) {
      if (value && key != 'routeId' && key != 'trailId') {
          queryArgs[key] = value
      }
    }
    if (!queryArgs.trailUuid){
      options.skip = true
    }     
    return query(queryArgs, options)
  }
}


export const useSearch = getData(api.useSearchQuery, {siteId: settings.SITE_ID, guideId: settings.GUIDE_ID, lang:settings.LANGUAGE})

export const useSiteMapsetting = getData(api.useGetSiteMapsettingsQuery, {siteId : settings.SITE_ID})

export const useGuide = getData(api.useGetSiteGuideQuery, {siteId: settings.SITE_ID, guideId: settings.GUIDE_ID, lang:settings.LANGUAGE})

export const useRoute = getData(api.useGetRouteQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE})

export const useRouteImage = getData(api.useGetRouteImagesQuery, {siteId : settings.SITE_ID})

export const useRouteMapsettings = getData(api.useGetRouteMapsettingsQuery, {siteId : settings.SITE_ID})

export const useRouteWaypointConnection = getData(api.useGetRouteWaypointConnectionQuery, {siteId: settings.SITE_ID})

export const useRouteNearestTrail = getData(api.useGetRouteNearestTrailQuery, {siteId: settings.SITE_ID})

export const useTrailDetails = addTrailUUID(getData(api.useGetTrailQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE}))

export const useTrailImages = addTrailUUID(getData(api.useGetTrailImagesQuery, {siteId : settings.SITE_ID}))

export const useTrailProfile = addTrailUUID(getData(api.useGetTrailProfileQuery, {siteId : settings.SITE_ID}))

export const useStepDetails =  addTrailUUID(getData(api.useGetStepDetailsQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE}))

export const useStepAmenities = addTrailUUID(getData(api.useGetStepAmenitiesQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE, page:1 }))

export const useTrailConnection = addTrailUUID(getData(api.useGetTrailConnectionQuery, {siteId : settings.SITE_ID}))

export const useTrailWaypointConnection = addTrailUUID(getData(api.useGetTrailWaypointConnectionQuery, {siteId : settings.SITE_ID}))

export const useTrail = (args, options) => {
  const {trailId, routeId} = args
  const route = useRoute({routeId: routeId}, options)
  const trail = useTrailDetails({routeId: routeId, trailId: trailId}, options)
  if (!route || !trail) return null
  return Object.assign({}, trail, route.trails[trailId - 1])
}

export const useSteps = (args, options) => {
  const trail = useTrailDetails(args, options)
  if (!trail) return null
  return trail.steps
}

export const useAlternativeStops = (args, options) => {
  const Trail = useTrailDetails(args, options)
  return (Trail) ? Trail.alternative_stops : null
}

export const useStep = (args, options) => {
  const {routeId, trailId, stepId} = args
  const steps = useSteps({routeId: routeId, trailId: trailId}, options)
  const details = useStepDetails({routeId: routeId, trailId: trailId, stepId: stepId}, options)

  if (!details || !steps) {
    return null
  }
  const step = steps[stepId - 1]
  return Object.assign({}, step, details)
}

export const useWaypoint = getData(api.useGetWaypointQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE})

export const useWaypoints = getData(api.useGetWaypointsQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE})

export const useWaypointAmenities = getData(api.useGetWaypointAmenitiesQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE, page:1 })

export const usePoi = getData(api.useGetPoiQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE})

export const useAccommodation =  getData(api.useGetAccommodationQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE})

export const useAccommodations = getData(api.useGetAccommodationsQuery, {siteId : settings.SITE_ID, lang:settings.LANGUAGE, page:1})

export const useSetRouteTrailStart = getActionData(api.useLazySetRouteTrailStartQuery, {siteId: settings.SITE_ID})

export const useSetRouteTrailStop = getActionData(api.useLazySetRouteTrailStopQuery, {siteId: settings.SITE_ID})

export const useSplitRouteTrail = getActionData(api.useLazySplitRouteTrailQuery, {siteId: settings.SITE_ID})

export const useAddRouteCheckpoint = getActionData(api.useLazyAddRouteCheckpointQuery, {siteId: settings.SITE_ID})

export const useRemoveRouteCheckpoint = getActionData(api.useLazyRemoveRouteCheckpointQuery, {siteId: settings.SITE_ID})

export const useAddTrailCheckpoint = getActionData(api.useLazyAddTrailCheckpointQuery, {siteId: settings.SITE_ID})

export const useCreateTrail = getActionData(api.useLazyCreateTrailQuery, {siteId: settings.SITE_ID})

export default api;
